import { useCallback, useState } from "react";
import { sendMahnungSharedEmail } from "@/lib/email";
import { buildShareLink } from "@/lib/utils/build-share-link";
import { OrganizationType } from "@/lib/types/types";
import { getReminderStageLabel } from "@/lib/utils/reminder-stage-label";
import { ShareEmailDialog } from "../../../../components/share-email-dialog/share-email-dialog.component";
import { environment } from "../../../../lib/utils/environment";
import { useCentralStore } from "../../../../store/Central";
import { useRemindersStore } from "../../../../store/Reminders";
import { PaymentReminderHistoryRow } from "../payment-reminder.types";

interface ShareEmailTarget {
	fileId: string;
	docNumber: string;
	stage: number;
	recipientEmail: string;
	recipientOrgType: OrganizationType | null;
}

export const useReminderShare = (onChanged: () => void) => {
	const [shareTarget, setShareTarget] = useState<ShareEmailTarget | null>(
		null
	);

	const toggleReminderShare = useCallback(
		async (row: PaymentReminderHistoryRow) => {
			const store = useRemindersStore.getState();
			if (row.shared_at) {
				if (await store.unshareReminder(row.reminder_id)) onChanged();
				return;
			}
			if (!row.file_id) return;
			const result = await store.shareReminder(
				row.reminder_id,
				row.file_id
			);
			onChanged();
			if (!result || result.reshared || !result.email) return;
			setShareTarget({
				fileId: row.file_id,
				docNumber: row.doc_number,
				stage: row.reminder_stage,
				recipientEmail: result.email,
				recipientOrgType: result.organizationType,
			});
		},
		[onChanged]
	);

	const handleSendEmail = async (message: string | undefined) => {
		const organization = useCentralStore.getState().organization;
		if (!shareTarget || !organization?.id) return false;
		return sendMahnungSharedEmail({
			recipientEmail: shareTarget.recipientEmail,
			labName: organization.name ?? "",
			reminderLabel: getReminderStageLabel(shareTarget.stage),
			invoiceNumber: shareTarget.docNumber,
			link: buildShareLink({
				fileId: shareTarget.fileId,
				recipientEmail: shareTarget.recipientEmail,
				senderOrgId: organization.id,
				recipientOrgType: shareTarget.recipientOrgType,
				dentconnectUrl: environment.VITE_DENTCONNECT_URL,
			}),
			message,
		});
	};

	const shareEmailDialog = (
		<ShareEmailDialog
			open={shareTarget !== null}
			title="Mahnung wurde geteilt"
			description="Möchten Sie den Kunden per E-Mail benachrichtigen?"
			ariaLabel="E-Mail-Benachrichtigung für geteilte Mahnung"
			testIdPrefix="reminder-share-email"
			onSend={handleSendEmail}
			onClose={() => setShareTarget(null)}
		/>
	);

	return { toggleReminderShare, shareEmailDialog };
};
